import { Fragment, useEffect, useRef, useState } from "react";
import { Box, Collapse, Grid } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { featuresData } from "@/utils/staticData/dataConstants";
import {
  FIRST_STEP_IDX,
  STEP_POSITIONS,
  TOTAL_STEPS_IDX,
} from "../utils/constants";
import { useFeatureMedia } from "../utils/useFeatureMedia";

import FeatureCard from "@/components/Features/components/FeatureCard";
import FeatureSummary from "../FeatureSummary";

const FeaturesList = () => {
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(FIRST_STEP_IDX);

  const { isMediumScreen, isLargeScreen } = useFeatureMedia();
  const featuresListRef = useRef<HTMLElement>();
  const containerRef = useRef<HTMLElement>();

  // Get feature from query params and go to the specified feature
  useEffect(() => {
    if (featuresListRef.current) {
      const currentStep = searchParams?.get("feature");

      if (currentStep) {
        setTimeout(() => {
          if (featuresListRef.current) {
            const currentPosition =
              featuresListRef.current.offsetTop + STEP_POSITIONS[currentStep];

            window.scrollTo({
              top: currentPosition,
              behavior: "smooth",
            });
          }
        }, 500);
      }
    }
  }, [searchParams]);

  // Handling scroll events
  useEffect(() => {
    const handleScroll = () => {
      if (featuresListRef.current && containerRef.current) {
        const value =
          featuresListRef.current.offsetTop - containerRef.current.offsetTop;

        if (value >= 0 && value < 1200) {
          const step = Math.floor(value / 300);

          if (step <= TOTAL_STEPS_IDX) {
            setActiveStep(step);
          }
        }
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup function to remove scroll event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeStep]);

  return (
    <>
      <Box sx={{ height: "2400px" }} ref={containerRef}>
        <Box ref={featuresListRef} sx={{ position: "sticky", top: 0 }}>
          <Grid container spacing={{ xs: 1, md: 2, xl: 4 }}>
            {featuresData.map((data, index) => (
              <Fragment key={index}>
                {isMediumScreen ? (
                  // Features for medium screens
                  <Grid item xs={12} key={index}>
                    {/* Feature summary */}
                    <Collapse in={activeStep < index} timeout="auto">
                      <FeatureSummary
                        title={data.title}
                        isActive={activeStep === index}
                      />
                    </Collapse>
                    {/* Feature card */}
                    <Collapse in={activeStep === index} timeout="auto">
                      <FeatureCard
                        {...data}
                      />
                    </Collapse>
                  </Grid>
                ) : !isLargeScreen ? (
                  // Feature for large screens
                  <Grid item xs={12} key={index}>
                    {/* Feature summary */}
                    <Collapse in={activeStep !== index} timeout="auto">
                      <FeatureSummary
                        title={data.title}
                        isActive={activeStep === index}
                      />
                    </Collapse>
                    {/* Feature card */}
                    <Collapse in={activeStep === index} timeout="auto">
                      <FeatureCard
                        {...data}
                      />
                    </Collapse>
                  </Grid>
                ) : (
                  // Features for mobile
                  <Grid
                    item
                    xs={12}
                    md={activeStep === index ? 8 : 1.3}
                    key={index}
                  >
                    <Box
                      display="flex"
                      sx={{
                        bgcolor: "background.default",
                        borderRadius: "20px",
                      }}
                    >
                      {/* Feature summary */}
                      <Collapse
                        orientation="horizontal"
                        in={activeStep !== index}
                        timeout="auto"
                      >
                        <FeatureSummary
                          title={data.title}
                          isActive={activeStep === index}
                        />
                      </Collapse>
                      {/* Feature card */}
                      <Collapse
                        orientation="horizontal"
                        in={activeStep === index}
                        timeout="auto"
                      >
                        <FeatureCard
                          {...data}
                        />
                      </Collapse>
                    </Box>
                  </Grid>
                )}
              </Fragment>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default FeaturesList;
